import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import useSWR from 'swr';
import { useOptionalAuthContext } from '../context/AuthContext';
import { useClient } from './useClient';
import storage from '../storage/storage';
import { PaginatedResponse } from '../types/serverTable';
import { SortState, TableColumn } from '../types/responsiveTable';
import { startOfDay, endOfDay } from 'date-fns';
import { MAX_EXPORT_PAGE_SIZE } from '../config/pagination';
import {
  TableView,
  hasTableParams,
  isTableParam,
  parseSavedView,
  parseTableView,
  serializeTableView,
  tableViewStorageKey,
  toQueryString,
  toSavedView,
  writeTableParams,
} from '../utils/tableView';

interface UseServerTableOptions<T extends Record<string, any>> {
  endpoint: string;
  initialPageSize?: number;
  debounceMs?: number;
  refreshInterval?: number;
  /**
   * Opts the table into the view sync: the view (filters, sort, page, page size) is read once from the URL, else from the
   * saved view, and every committed change is written to the URL (replace) and to storage. Without it the hook never
   * touches the URL or storage.
   */
  tableId?: string;
  /** The columns the URL and the saved view are parsed against. Needed with `tableId`. */
  columns?: ReadonlyArray<TableColumn<T>>;
  /** Called with the view the hook took from the URL or from storage (at the start, and on a URL change made by someone else). */
  onViewRestored?: (view: TableView) => void;
}

interface UseServerTableReturn<T extends Record<string, any>> {
  data: T[];
  pagination: PaginatedResponse<T>['pagination'] | undefined;
  filterOptions: PaginatedResponse<T>['filterOptions'] | undefined;
  isLoading: boolean;
  /** The request of the current view failed and the rows held from an earlier view are not its answer (#421). */
  loadFailed: boolean;
  filters: Record<string, any>;
  sortState: SortState;
  page: number;
  pageSize: number;
  handlePageChange: (newPage: number, newPageSize?: number) => void;
  handleFiltersChange: (newFilters: Record<string, any>) => void;
  handleSortChange: (newSort: SortState) => void;
  refresh: () => void;
  /** Asks for the current view again, ignoring any argument (a click event must not become the answer). */
  retry: () => void;
  /**
   * Fetches every row matching the currently applied filters and sort, without
   * pagination. Used to export the table to Excel with the same filters the
   * user sees on screen (the backend raises its pageSize cap for this request).
   */
  fetchAllForExport: () => Promise<T[]>;
}

/** The committed view: it drives the request, the URL and the saved view. The filters shown by the inputs live apart. */
interface ViewState {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  filters: Record<string, any>;
  sort: SortState;
  page: number;
  pageSize: number;
}

const NO_SORT: SortState = { field: null, direction: 'none' };

const sameJson = (a: unknown, b: unknown): boolean => JSON.stringify(a) === JSON.stringify(b);

/** The table params of the URL as written. Invalid ones make it differ from every view, so step 2 writes the clean URL. */
const tableQuery = (params: URLSearchParams): string =>
  hasTableParams(params) ? toQueryString(Array.from(params).filter(([key]) => isTableParam(key))) : '';

export function useServerTable<T extends Record<string, any>>({
  endpoint,
  initialPageSize = 25,
  debounceMs = 300,
  refreshInterval,
  tableId,
  columns,
  onViewRestored,
}: UseServerTableOptions<T>): UseServerTableReturn<T> {
  const { get } = useClient();
  // The table's own requests throw on failure, so SWR records the error instead of an empty answer (#421)
  const { get: getOrThrow } = useClient({ throwOnError: true });
  // The last answer of each view, by url
  const answers = useRef(new Map<string, { url: string; page: PaginatedResponse<T> }>());
  const location = useLocation();
  const navigate = useNavigate();
  const auth = useOptionalAuthContext();
  const user = auth?.user;
  // The saved view is keyed by the user: wait while the session loads, but not when nobody is signed in.
  const authPending = !!auth && !user && (auth.isAuthenticating || auth.userLoading);
  const userId = user?.id;
  const organizationId = user?.organizationId;

  const defaultView = (): ViewState => ({ filters: {}, sort: NO_SORT, page: 1, pageSize: initialPageSize });
  const [view, setView] = useState<ViewState>(defaultView);
  // What the filter inputs show. It becomes part of `view` after the debounce.
  const [draftFilters, setDraftFilters] = useState<Record<string, any>>({});
  // With a tableId nothing is requested until the view has been read from the URL or from storage.
  const [ready, setReady] = useState(!tableId);

  // Refs, so the effects below do not run again when a page rebuilds its columns or its callback on every render.
  const columnsRef = useRef<ReadonlyArray<TableColumn<T>>>(columns ?? []);
  columnsRef.current = columns ?? [];
  const locationRef = useRef(location);
  locationRef.current = location;
  const onViewRestoredRef = useRef(onViewRestored);
  onViewRestoredRef.current = onViewRestored;
  const viewRef = useRef(view);
  viewRef.current = view;
  // The canonical query of the view the URL is known to show. The table's own writes come back equal to it and are ignored.
  const syncedRef = useRef('');

  const storageKey = tableId && userId && organizationId ? tableViewStorageKey(userId, organizationId, tableId) : null;
  const canonical = useMemo(
    () => (tableId && ready ? toQueryString(serializeTableView(view as TableView, columnsRef.current)) : ''),
    [tableId, ready, view],
  );
  const savedJson = useMemo(
    () => (tableId && ready ? toSavedView(view as TableView, columnsRef.current) : ''),
    [tableId, ready, view],
  );

  // The pending commit of the filters shown. A view taken from the URL or from storage replaces them, so it must not fire later.
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  const applyView = useCallback((next: ViewState) => {
    clearTimeout(debounceRef.current);
    setView(next);
    setDraftFilters(next.filters);
  }, []);

  // Step 5: the filters shown become the committed filters after the debounce. Keep it before steps 1 and 4: their `applyView`
  // must cancel the timer this effect starts in the same commit, or a slow render lets it replace the view of the URL (#518).
  useEffect(() => {
    const timer = setTimeout(() => {
      setView((current) => (sameJson(current.filters, draftFilters) ? current : { ...current, filters: draftFilters, page: 1 }));
    }, debounceMs);
    debounceRef.current = timer;

    return () => clearTimeout(timer);
  }, [draftFilters, debounceMs]);

  // Step 1: read the view once. A non-empty endpoint means the columns of the page are final.
  useEffect(() => {
    if (!tableId || ready || !endpoint || authPending) return;
    let cancelled = false;
    (async () => {
      const cols = columnsRef.current;
      const params = new URLSearchParams(locationRef.current.search);
      const fromUrl = parseTableView(params, cols);
      const fromUrlCanonical = toQueryString(serializeTableView(fromUrl, cols));
      let initial: ViewState = fromUrl;
      // Only a URL with a usable table param wins: a link with only invalid ones (`?page=abc`) opens the saved view.
      // With no signed-in user there is no saved view, and the table still loads.
      if (fromUrlCanonical === '' && storageKey) {
        let saved: TableView | null = null;
        try {
          saved = parseSavedView(await storage.getItem(storageKey), cols);
        } catch {
          saved = null;
        }
        initial = saved ?? defaultView();
      }
      if (cancelled) return;
      syncedRef.current = tableQuery(params);
      applyView(initial);
      setReady(true);
      onViewRestoredRef.current?.(initial as TableView);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableId, ready, endpoint, storageKey, authPending, applyView]);

  // Step 2: write a committed change to the URL.
  useEffect(() => {
    if (!tableId || !ready || canonical === syncedRef.current) return;
    syncedRef.current = canonical;
    const { pathname, search, hash, state } = locationRef.current;
    navigate(
      { pathname, search: writeTableParams(search, viewRef.current as TableView, columnsRef.current), hash },
      { replace: true, state },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, canonical]);

  // Step 3: save the view. It also runs after step 1, so a view from the URL replaces the saved one.
  useEffect(() => {
    if (!tableId || !ready || !storageKey) return;
    storage.setItem(storageKey, savedJson).catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, savedJson]);

  // Step 4: follow a URL change made by someone else (links, Back and Forward). Not `ready`: its own flip is not a change.
  useEffect(() => {
    if (!tableId || !ready) return;
    const cols = columnsRef.current;
    const params = new URLSearchParams(location.search);
    const parsed = parseTableView(params, cols);
    const parsedCanonical = toQueryString(serializeTableView(parsed, cols));
    if (parsedCanonical === syncedRef.current) return;
    // No usable table param (the menu entry of the open table, or Back to such an entry): the saved view opens, as in step 1
    // and as on a reload (AC-11), so Back cannot return to an older view kept in no URL. Step 3 keeps the saved view equal to
    // the current view without its text filters and page, so it is read from there and not from storage: a view saved
    // later by another tab is not seen here.
    const next =
      parsedCanonical === '' ? (parseSavedView(toSavedView(viewRef.current as TableView, cols), cols) ?? defaultView()) : parsed;
    applyView(next);
    onViewRestoredRef.current?.(next as TableView);
    // Written here, not by step 2: when `next` equals the current view, `canonical` does not change and step 2 does not run.
    const nextCanonical = toQueryString(serializeTableView(next as TableView, cols));
    syncedRef.current = nextCanonical;
    if (nextCanonical !== tableQuery(params)) {
      const { pathname, hash, state } = locationRef.current;
      navigate({ pathname, search: writeTableParams(location.search, next as TableView, cols), hash }, { replace: true, state });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const buildFiltersParam = useCallback((filtersToSerialize: Record<string, any>): string | null => {
    if (Object.keys(filtersToSerialize).length === 0) return null;

    const transformedFilters: Record<string, any> = {};
    Object.entries(filtersToSerialize).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        let filteredValue = value.filter((v) => v !== undefined);

        if (filteredValue.length === 0) {
          return;
        }

        if (filteredValue.length > 0 && filteredValue[0] instanceof Date) {
          filteredValue = filteredValue.map((date, index) => {
            if (index === 0) {
              return startOfDay(date).toISOString();
            }
            return endOfDay(date).toISOString();
          });
        }

        transformedFilters[key] = filteredValue;
      } else if (value !== undefined) {
        transformedFilters[key] = value;
      }
    });

    return Object.keys(transformedFilters).length > 0 ? JSON.stringify(transformedFilters) : null;
  }, []);

  const buildUrl = useCallback(
    (overrides: { page: number; pageSize: number; isExport?: boolean }) => {
      const params = new URLSearchParams();
      params.append('page', overrides.page.toString());
      params.append('pageSize', overrides.pageSize.toString());

      if (view.sort.field && view.sort.direction !== 'none') {
        params.append('sortBy', view.sort.field);
        params.append('sortDirection', view.sort.direction);
      }

      const filtersParam = buildFiltersParam(view.filters);
      if (filtersParam) {
        params.append('filters', filtersParam);
      }

      if (overrides.isExport) {
        params.append('export', 'true');
      }

      const separator = endpoint.includes('?') ? '&' : '?';
      return `${endpoint}${separator}${params.toString()}`;
    },
    [endpoint, view.sort, view.filters, buildFiltersParam],
  );

  const url = useMemo(() => buildUrl({ page: view.page, pageSize: view.pageSize }), [buildUrl, view.page, view.pageSize]);

  // Each answer carries the url it answers: keepPreviousData can show the answer of an earlier view
  const {
    data: answer,
    isLoading,
    isValidating,
    error,
    mutate,
  } = useSWR<{ url: string; page: PaginatedResponse<T> } | undefined>(
    ready && endpoint ? url : null,
    async (url: string) => {
      try {
        const answer = { url, page: (await getOrThrow(url)) as PaginatedResponse<T> };
        answers.current.set(url, answer);
        return answer;
      } catch (err) {
        // SWR stops polling a key with an error, so only a new view that fails becomes one (#421): a failed refresh keeps
        // its view's answer, a failed first load stays empty; a failed new view polls again after Reintentar or a change.
        const held = answers.current.get(url);
        if (held) return held;
        if (answers.current.size === 0) return undefined;
        throw err;
      }
    },
    {
      revalidateOnFocus: false,
      keepPreviousData: true,
      refreshInterval,
      // One request per change, as before: the next filter, sort or page asks again
      shouldRetryOnError: false,
    },
  );
  const data = answer?.page;
  const retry = useCallback(() => {
    void mutate();
  }, [mutate]);
  const loadFailed = !!error && !!answer && answer.url !== url;

  // Step 6: a page past the last one (the data changed, or an old link) goes to the last page, or to page 1 with no records.
  // Waiting for the end of the request keeps it from acting on the rows `keepPreviousData` still shows.
  const totalPages = data?.pagination?.totalPages;
  useEffect(() => {
    if (!tableId || !ready || isValidating || error || totalPages === undefined) return;
    const last = Math.max(1, totalPages);
    setView((current) => (current.page > last ? { ...current, page: last } : current));
  }, [tableId, ready, isValidating, error, totalPages]);

  const handlePageChange = useCallback((newPage: number, newPageSize?: number) => {
    setView((current) =>
      newPageSize && newPageSize !== current.pageSize
        ? { ...current, pageSize: newPageSize, page: 1 }
        : { ...current, page: newPage },
    );
  }, []);

  const handleFiltersChange = useCallback((newFilters: Record<string, any>) => {
    setDraftFilters(newFilters);
  }, []);

  const handleSortChange = useCallback((newSort: SortState) => {
    setView((current) => ({ ...current, sort: newSort, page: 1 }));
  }, []);

  const fetchAllForExport = useCallback(async (): Promise<T[]> => {
    const exportUrl = buildUrl({ page: 1, pageSize: MAX_EXPORT_PAGE_SIZE, isExport: true });
    // No timeout: a full export can take longer than the default of the client.
    const result = await get<PaginatedResponse<T>>(exportUrl, { timeout: 0 });
    return result?.data ?? [];
  }, [buildUrl, get]);

  return {
    data: data?.data || [],
    pagination: data?.pagination,
    filterOptions: data?.filterOptions,
    // Until the view is read there is no request yet, but the table is not empty: it is about to load.
    isLoading: isLoading || (!ready && !!endpoint),
    loadFailed,
    filters: draftFilters,
    sortState: view.sort,
    page: view.page,
    pageSize: view.pageSize,
    handlePageChange,
    handleFiltersChange,
    handleSortChange,
    refresh: mutate,
    retry,
    fetchAllForExport,
  };
}
